import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShoppingBag, User, Menu, X, Minus, Plus, Trash2, Search, ChevronDown } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MENU_COLUMNS = [
  { title: "Shop by", items: [["new-arrivals", "New Arrivals"], ["best-sellers", "Best Sellers"], ["gifts", "Gifts"], ["collections", "Collections"]] },
  { title: "For", items: [["men", "Men"], ["women", "Women"]] },
  { title: "Categories", items: [["bags", "Bags"], ["small-leather-goods", "Small Leather Goods"], ["shoes", "Shoes"], ["leather-clothing", "Leather Clothing"]] },
  { title: "Watches", items: [["watches-men", "Watches for Men"], ["watches-women", "Watches for Women"]] },
  { title: "Perfume", items: [["perfume-men", "Perfume for Men"], ["perfume-women", "Perfume for Women"]] },
  { title: "Occasion", items: [["travel", "Travel"], ["business", "Business"]] },
];
const FLAT_MENU = MENU_COLUMNS.flatMap((c) => c.items);

const NAV = [
  { to: "/craftsmanship", label: "Craftsmanship" },
  { to: "/journal", label: "Journal" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const SearchOverlay = ({ open, setOpen }) => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState({ products: [], posts: [] });
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setRes({ products: [], posts: [] }); return; }
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (to) => { setOpen(false); setQ(""); navigate(to); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-luxa-cream max-w-2xl top-24 translate-y-0 border-luxa-sand" data-testid="search-overlay">
        <DialogTitle className="sr-only">Search</DialogTitle>
        <DialogDescription className="sr-only">Search products and journal articles</DialogDescription>
        <div className="flex items-center gap-3 border-b border-luxa-sand pb-4">
          <Search size={18} strokeWidth={1.5} className="text-luxa-espresso/50" />
          <input autoFocus data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) go(`/shop?q=${encodeURIComponent(q.trim())}`); }}
            placeholder="Search wallets, bags, shoes, care guides…"
            className="flex-1 bg-transparent font-serif text-2xl placeholder:text-luxa-espresso/30 focus:outline-none" />
        </div>
        <div className="max-h-[55vh] overflow-y-auto">
          {res.products.length === 0 && res.posts.length === 0 && (
            <p className="py-8 overline text-luxa-espresso/40" data-testid="search-empty">
              {q.trim().length < 2 ? "Type at least two letters" : "Nothing matched that search"}
            </p>
          )}
          {res.products.map((p) => (
            <button key={p.id} data-testid={`search-result-${p.slug}`} onClick={() => go(`/product/${p.slug}`)}
              className="w-full flex items-center gap-4 py-4 border-b border-luxa-sand text-left group">
              <img src={p.images?.[0]} alt="" className="w-12 h-14 object-cover" />
              <span className="flex-1">
                <span className="block overline text-luxa-espresso/40">{p.category}</span>
                <span className="block font-serif text-lg group-hover:text-luxa-gold transition-colors duration-300">{p.name}</span>
              </span>
              <span className="text-sm">{money(p.price)}</span>
            </button>
          ))}
          {res.posts.map((p) => (
            <button key={p.id} data-testid={`search-post-${p.slug}`} onClick={() => go(`/journal/${p.slug}`)}
              className="w-full flex items-center gap-4 py-4 border-b border-luxa-sand text-left">
              <span className="overline text-luxa-gold">Journal</span>
              <span className="font-serif text-lg">{p.title}</span>
            </button>
          ))}
          {res.products.length > 0 && (
            <button data-testid="search-see-all" onClick={() => go(`/shop?q=${encodeURIComponent(q.trim())}`)}
              className="mt-5 overline text-luxa-espresso/60 link-underline">See all results →</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CartDrawer = () => {
  const { items, setQty, remove, subtotal, shipping, total, open, setOpen } = useCart();
  const navigate = useNavigate();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-luxa-cream border-l border-luxa-sand w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl font-normal text-luxa-espresso">Your Bag</SheetTitle>
        </SheetHeader>
        {items.length === 0 ? (
          <p className="mt-10 text-sm text-luxa-espresso/60" data-testid="cart-empty">Your bag is empty.</p>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto mt-6 space-y-6">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-4" data-testid={`cart-item-${i.product_id}`}>
                  <img src={i.image} alt={i.name} className="w-20 h-24 object-cover" />
                  <div className="flex-1">
                    <p className="font-serif text-lg leading-tight">{i.name}</p>
                    <p className="text-xs text-luxa-espresso/60 mt-1">{money(i.price)}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="p-1 border border-luxa-sand hover:border-luxa-brown" data-testid={`cart-dec-${i.product_id}`}><Minus size={12} strokeWidth={1.5} /></button>
                      <span className="text-sm w-4 text-center">{i.quantity}</span>
                      <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="p-1 border border-luxa-sand hover:border-luxa-brown" data-testid={`cart-inc-${i.product_id}`}><Plus size={12} strokeWidth={1.5} /></button>
                      <button onClick={() => remove(i.product_id)} className="ml-auto text-luxa-espresso/40 hover:text-luxa-gold" data-testid={`cart-remove-${i.product_id}`}><Trash2 size={14} strokeWidth={1.5} /></button>
                    </div>
                  </div>
                  <p className="text-sm">{money(i.price * i.quantity)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-luxa-sand pt-5 space-y-2">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
              <div className="flex justify-between text-sm text-luxa-espresso/60"><span>Shipping</span><span>{shipping === 0 ? "Complimentary" : money(shipping)}</span></div>
              <div className="flex justify-between font-serif text-xl pt-2"><span>Total</span><span data-testid="cart-total">{money(total)}</span></div>
              <Button data-testid="checkout-button" className="w-full rounded-none bg-luxa-brown hover:bg-luxa-brown/90 text-white h-12 overline mt-3"
                onClick={() => { setOpen(false); navigate("/checkout"); }}>Proceed to Checkout</Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const Header = () => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const [mobile, setMobile] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    api.get("/menu")
      .then((r) => setCounts(Object.fromEntries(r.data.map((m) => [m.slug, m.count]))))
      .catch(() => {});
  }, []);
  const has = (slug) => !counts || counts[slug] > 0;
  const columns = MENU_COLUMNS
    .map((c) => ({ ...c, items: c.items.filter(([slug]) => has(slug)) }))
    .filter((c) => c.items.length > 0);

  return (
    <>
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-luxa-cream/85 border-b border-luxa-sand">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-20 flex items-center gap-8">
          <button className="lg:hidden" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {mobile ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
          <Link to="/" data-testid="logo-link" className="font-serif text-2xl tracking-[0.2em] text-luxa-espresso">
            TRUST<span className="text-luxa-brown"> LUXA</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-9 ml-10">
            <div className="relative" onMouseEnter={() => setShopOpen(true)} onMouseLeave={() => setShopOpen(false)}>
              <Link to="/shop" data-testid="nav-shop"
                className="overline text-luxa-espresso/70 hover:text-luxa-espresso flex items-center gap-1.5">
                Shop <ChevronDown size={13} strokeWidth={1.5} className={shopOpen ? "rotate-180 transition-transform duration-300" : "transition-transform duration-300"} />
              </Link>
              {shopOpen && (
                <div data-testid="shop-mega-menu"
                  className="absolute left-0 top-full pt-5 w-[980px] animate-fade-up">
                  <div className="bg-luxa-cream border border-luxa-sand p-9 grid gap-7 shadow-[0_24px_60px_-30px_rgba(10,10,10,0.35)]"
                    style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
                    {columns.map((col) => (
                      <div key={col.title}>
                        <p className="overline text-luxa-gold">{col.title}</p>
                        <div className="mt-4 flex flex-col gap-3">
                          {col.items.map(([slug, label]) => (
                            <Link key={slug} to={`/shop?menu=${slug}`} data-testid={`menu-${slug}`}
                              onClick={() => setShopOpen(false)}
                              className="text-sm text-luxa-espresso/70 hover:text-luxa-gold transition-colors duration-300">
                              {label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `overline link-underline ${isActive ? "text-luxa-brown" : "text-luxa-espresso/70 hover:text-luxa-espresso"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-6">
            <button onClick={() => setSearchOpen(true)} data-testid="search-toggle" aria-label="Search" className="hover:text-luxa-gold">
              <Search size={19} strokeWidth={1.5} />
            </button>
            <Link to="/track" className="hidden sm:block overline text-luxa-espresso/70 hover:text-luxa-gold" data-testid="nav-track">Track</Link>
            <Link to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"} data-testid="account-link" aria-label="Account" className="hover:text-luxa-gold">
              <User size={19} strokeWidth={1.5} />
            </Link>
            <button onClick={() => setOpen(true)} data-testid="cart-toggle" aria-label="Cart" className="relative hover:text-luxa-gold">
              <ShoppingBag size={19} strokeWidth={1.5} />
              {count > 0 && <span data-testid="cart-count" className="absolute -top-2 -right-2 bg-luxa-brown text-luxa-cream text-[10px] w-4 h-4 flex items-center justify-center rounded-full">{count}</span>}
            </button>
          </div>
        </div>
        {mobile && (
          <nav className="lg:hidden border-t border-luxa-sand px-5 py-5 flex flex-col gap-4 bg-luxa-cream max-h-[70vh] overflow-y-auto">
            <p className="overline text-luxa-gold">Shop</p>
            {FLAT_MENU.filter(([slug]) => has(slug)).map(([slug, label]) => (
              <Link key={slug} to={`/shop?menu=${slug}`} onClick={() => setMobile(false)}
                data-testid={`mobile-menu-${slug}`} className="text-sm text-luxa-espresso/75">{label}</Link>
            ))}
            <p className="overline text-luxa-gold mt-3">House</p>
            {NAV.concat([{ to: "/track", label: "Track Order" }]).map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMobile(false)} className="text-sm text-luxa-espresso/75">{n.label}</Link>
            ))}
          </nav>
        )}
      </header>
      <SearchOverlay open={searchOpen} setOpen={setSearchOpen} />
      <CartDrawer />
    </>
  );
};

export const Footer = () => {
  const [email, setEmail] = useState("");
  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the atelier list.");
      setEmail("");
    } catch { toast.error("Please enter a valid email."); }
  };
  return (
    <footer className="mt-32 bg-luxa-espresso text-luxa-cream">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid gap-14 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
        <div>
          <span aria-hidden="true" className="inline-flex items-center justify-center w-12 h-12 border border-luxa-gold/60 font-serif text-luxa-gold text-lg tracking-[0.1em]">TL</span>
          <p className="mt-5 font-serif text-2xl tracking-[0.2em]">TRUST LUXA</p>
          <p className="mt-5 text-sm text-luxa-cream/60 max-w-xs leading-relaxed">
            Premium 100% leather, cut and saddle-stitched by hand. Built on trust, durability and elegance —
            objects meant to outlive their owners.
          </p>
        </div>
        <div>
          <p className="overline text-luxa-gold">Shop</p>
          <div className="mt-5 flex flex-col gap-3 text-sm text-luxa-cream/70">
            <Link to="/shop?menu=new-arrivals" className="hover:text-luxa-gold">New Arrivals</Link>
            <Link to="/shop?menu=best-sellers" className="hover:text-luxa-gold">Best Sellers</Link>
            <Link to="/shop?menu=bags" className="hover:text-luxa-gold">Bags</Link>
            <Link to="/shop?menu=small-leather-goods" className="hover:text-luxa-gold">Small Leather Goods</Link>
            <Link to="/shop?menu=business" className="hover:text-luxa-gold">Business</Link>
            <Link to="/shop?menu=watches" className="hover:text-luxa-gold">Watches</Link>
            <Link to="/shop?menu=perfume" className="hover:text-luxa-gold">Perfume</Link>
            <Link to="/shop?menu=leather-clothing" className="hover:text-luxa-gold">Leather Clothing</Link>
            <Link to="/shop?menu=gifts" className="hover:text-luxa-gold">Gifts</Link>
          </div>
        </div>
        <div>
          <p className="overline text-luxa-gold">House</p>
          <div className="mt-5 flex flex-col gap-3 text-sm text-luxa-cream/70">
            <Link to="/about" className="hover:text-luxa-gold">About</Link>
            <Link to="/craftsmanship" className="hover:text-luxa-gold">Craftsmanship</Link>
            <Link to="/journal" className="hover:text-luxa-gold">Journal</Link>
            <Link to="/contact" className="hover:text-luxa-gold">Contact</Link>
          </div>
        </div>
        <div>
          <p className="overline text-luxa-gold">Atelier Letters</p>
          <form onSubmit={subscribe} className="mt-5 flex gap-2" data-testid="newsletter-form">
            <Input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email" className="rounded-none bg-transparent border-luxa-cream/25 text-luxa-cream placeholder:text-luxa-cream/40 h-11" />
            <Button data-testid="newsletter-submit" type="submit" className="rounded-none bg-luxa-gold text-luxa-espresso hover:bg-luxa-gold/90 h-11 overline px-5">Join</Button>
          </form>
          <p className="mt-3 text-xs text-luxa-cream/40">Care notes and early access. No noise.</p>
        </div>
      </div>
      <div className="border-t border-luxa-cream/10 px-5 sm:px-8 py-6 text-xs text-luxa-cream/40 max-w-[1400px] mx-auto flex flex-wrap gap-3 justify-between">
        <span>© {new Date().getFullYear()} Trust Luxa. All rights reserved.</span>
        <span>Stripe &middot; Cash on Delivery &middot; USD</span>
      </div>
    </footer>
  );
};
